import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { getCategories, saveProduct } from "../../utils/storage";

const BADGE_OPTIONS = [
  "Best Seller",
  "New Arrival",
  "Industrial Grade",
  "Trending",
  "Limited Stock",
  "Featured",
];

const INITIAL_FORM = {
  productsName: "",
  category: null,
  badge: "",
  shortDescription: "",
  description: "",
  highlights: "",
  mrp: "",
  salePrice: "",
  discount: "",
  brand: "",
  availability: "",
  warranty: "",
  metaTitle: "",
  metaDescription: "",
  seoUrl: "",
};

const INITIAL_FILES = { image1: null, image2: null, image3: null };

const AddProducts = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [imageFiles, setImageFiles] = useState(INITIAL_FILES);
  const [imagePreviews, setImagePreviews] = useState({});
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setCategories(getCategories());
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    let error = "";
    if (file) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
        error = "Only JPG, PNG, WEBP allowed";
      if (file.size > 2 * 1024 * 1024) error = "Max file size is 2MB";
    }
    setImageFiles((prev) => ({ ...prev, [name]: file || null }));
    setErrors((prev) => ({ ...prev, [name]: error }));
    // Preview
    if (file && !error) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setImagePreviews((prev) => ({ ...prev, [name]: ev.target.result }));
      reader.readAsDataURL(file);
    } else {
      setImagePreviews((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const temp = {};

    if (!formData.productsName.trim()) temp.productsName = "Product name is required";
    else if (formData.productsName.trim().length < 3)
      temp.productsName = "Minimum 3 characters required";

    if (!formData.category) temp.category = "Category is required";
    if (!formData.badge) temp.badge = "Badge is required";

    if (!formData.shortDescription.trim())
      temp.shortDescription = "Short description is required";
    else if (formData.shortDescription.trim().length < 10)
      temp.shortDescription = "Minimum 10 characters required";
    else if (formData.shortDescription.trim().length > 400)
      temp.shortDescription = "Maximum 400 characters required";

    const plainDesc = formData.description.replace(/<(.|\n)*?>/g, "").trim();
    if (!plainDesc) temp.description = "Description is required";
    else if (plainDesc.length < 20) temp.description = "Minimum 20 characters required";
    else if (plainDesc.length >1000) temp.description = "Maximum 1000 characters required";

    const plainHL = formData.highlights.replace(/<(.|\n)*?>/g, "").trim();
    if (!plainHL) temp.highlights = "Highlights are required";
    else if (plainHL.length < 10) temp.highlights = "Minimum 10 characters required";
    else if (plainHL.length > 100) temp.highlights = "Maximum 100 characters allowed";

    if (!formData.mrp.trim()) temp.mrp = "MRP is required";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.mrp)) temp.mrp = "Enter valid price";

    if (!formData.salePrice.trim()) temp.salePrice = "Sale price is required";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.salePrice))
      temp.salePrice = "Enter valid sale price";
    else if (Number(formData.salePrice) > Number(formData.mrp))
      temp.salePrice = "Sale price cannot exceed MRP";

    if (!formData.discount.trim()) temp.discount = "Discount is required";
    else if (!/^\d+$/.test(formData.discount)) temp.discount = "Only numbers allowed";
    else if (Number(formData.discount) < 0 || Number(formData.discount) > 100)
      temp.discount = "Discount must be between 0–100";

    if (!formData.brand.trim()) temp.brand = "Brand is required";
    else if (!/^[A-Za-z0-9\s&.-]+$/.test(formData.brand)) temp.brand = "Invalid brand name";

    if (!formData.availability.trim()) temp.availability = "Availability is required";
    else if (
      !/^(in stock|out of stock|limited stock)$/i.test(formData.availability.trim())
    )
      temp.availability = "Use: In Stock / Out of Stock / Limited Stock";

    if (!formData.warranty.trim()) temp.warranty = "Warranty is required";

    if (!formData.metaTitle.trim()) temp.metaTitle = "Meta title is required";
    else if (formData.metaTitle.length > 60) temp.metaTitle = "Max 60 characters";

    if (!formData.metaDescription.trim())
      temp.metaDescription = "Meta description is required";
    else if (formData.metaDescription.length > 160)
      temp.metaDescription = "Max 160 characters";

    if (!formData.seoUrl.trim()) temp.seoUrl = "SEO URL is required";
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.seoUrl))
      temp.seoUrl = "Lowercase, numbers and hyphens only";

    ["image1", "image2", "image3"].forEach((img, i) => {
      if (!imageFiles[img]) temp[img] = `Image ${i + 1} is required`;
    });

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await saveProduct(formData, imageFiles);
      setOpen(true);
      setFormData(INITIAL_FORM);
      setImageFiles(INITIAL_FILES);
      setImagePreviews({});
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  };

  const quillBoxSx = {
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#fff",
    "& .ql-toolbar": {
      border: "none",
      borderBottom: "1px solid #eee",
      backgroundColor: "#fafafa",
      padding: "6px",
    },
    "& .ql-container": { border: "none", height: "160px" },
    "& .ql-editor": { height: "160px", fontSize: "14px", padding: "12px", overflowY: "auto" },
  };

  const highlightsCharCount = formData.highlights.replace(/<(.|\n)*?>/g, "").trim().length;

  return (
    <Box sx={{ p: 3, background: "#f4f6fb", minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h5" fontWeight="600" color="#333">
          Add Product
        </Typography>
        <Button onClick={() => navigate("/admin/products")}>BACK TO LIST</Button>
      </Box>

      <Paper sx={{ p: 5, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <Box component="form" onSubmit={handleSubmit}>

          {/* Row 1 — Name + Category */}
          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <TextField
                fullWidth
                label="Product Name *"
                name="productsName"
                value={formData.productsName}
                onChange={handleChange}
                size="small"
                error={!!errors.productsName}
                helperText={errors.productsName}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "280px" }}>
              <Autocomplete
                options={categories}
                getOptionLabel={(o) => o?.name || ""}
                value={formData.category}
                onChange={(_, newVal) => {
                  setFormData((prev) => ({ ...prev, category: newVal }));
                  setErrors((prev) => ({ ...prev, category: "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category *"
                    size="small"
                    error={!!errors.category}
                    helperText={
                      errors.category ||
                      (categories.length === 0
                        ? "No categories yet — add one first"
                        : "")
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Row 2 — Badge */}
          <Box sx={{ mb: 3, maxWidth: "300px" }}>
            <TextField
              select
              fullWidth
              label="Badge *"
              name="badge"
              value={formData.badge}
              onChange={handleChange}
              size="small"
              error={!!errors.badge}
              helperText={errors.badge || "Shown on the product card"}
            >
              {BADGE_OPTIONS.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Short Description */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Short Description *"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              rows={2}
              multiline
              error={!!errors.shortDescription}
              helperText={errors.shortDescription}
            />
          </Box>

          {/* Description */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#222">
              Description *
            </Typography>
            <Box sx={quillBoxSx}>
              <ReactQuill
                theme="snow"
                modules={modules}
                value={formData.description}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, description: v }));
                  setErrors((prev) => ({ ...prev, description: "" }));
                }}
              />
            </Box>
            {errors.description && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                {errors.description}
              </Typography>
            )}
          </Box>

          {/* Prices */}
          <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
            {[
              { label: "MRP *", name: "mrp" },
              { label: "Sale Price *", name: "salePrice" },
              { label: "Discount (%) *", name: "discount" },
            ].map(({ label, name }) => (
              <Box key={name} sx={{ flex: 1, minWidth: "220px" }}>
                <TextField
                  fullWidth
                  label={label}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  size="small"
                  error={!!errors[name]}
                  helperText={errors[name]}
                />
              </Box>
            ))}
          </Box>

          {/* Highlights */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#222">
              Highlights *
            </Typography>
            <Box sx={quillBoxSx}>
              <ReactQuill
                theme="snow"
                modules={modules}
                value={formData.highlights}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, highlights: v }));
                  setErrors((prev) => ({ ...prev, highlights: "" }));
                }}
              />
            </Box>
            {errors.highlights && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                {errors.highlights}
              </Typography>
            )}
            <Typography
              variant="caption"
              color={highlightsCharCount > 500 ? "error" : "text.secondary"}
              sx={{ mt: 0.5, display: "block" }}
            >
              {highlightsCharCount}/500
            </Typography>
          </Box>

          {/* Brand / Availability / Warranty */}
          <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
            {[
              { label: "Brand *", name: "brand" },
              {
                label: "Availability *",
                name: "availability",
                helper: "In Stock / Out of Stock / Limited Stock",
              },
              { label: "Warranty *", name: "warranty" },
            ].map(({ label, name, helper }) => (
              <Box key={name} sx={{ flex: 1, minWidth: "220px" }}>
                <TextField
                  fullWidth
                  label={label}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  size="small"
                  error={!!errors[name]}
                  helperText={errors[name] || helper || ""}
                />
              </Box>
            ))}
          </Box>

          {/* Meta */}
          <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "260px" }}>
              <TextField
                fullWidth
                label="Meta Title *"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                size="small"
                error={!!errors.metaTitle}
                helperText={errors.metaTitle || `${formData.metaTitle.length}/60`}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "260px" }}>
              <TextField
                fullWidth
                label="Meta Description *"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                size="small"
                error={!!errors.metaDescription}
                helperText={
                  errors.metaDescription || `${formData.metaDescription.length}/160`
                }
              />
            </Box>
          </Box>

          {/* SEO URL */}
          <Box sx={{ mb: 3, maxWidth: "420px" }}>
            <TextField
              fullWidth
              label="SEO URL *"
              name="seoUrl"
              value={formData.seoUrl}
              onChange={handleChange}
              size="small"
              error={!!errors.seoUrl}
              helperText={errors.seoUrl}
            />
          </Box>

          {/* Images */}
          <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
            {["image1", "image2", "image3"].map((key, i) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  minWidth: "200px",
                  border: "1px dashed #d0d5dd",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Image {i + 1} *
                </Typography>
                {imagePreviews[key] && (
                  <Box
                    component="img"
                    src={imagePreviews[key]}
                    alt={`Preview ${i + 1}`}
                    sx={{
                      width: "100%",
                      height: "120px",
                      objectFit: "contain",
                      mb: 1,
                      borderRadius: 1,
                      background: "#fff",
                    }}
                  />
                )}
                <input
                  type="file"
                  name={key}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  style={{ width: "100%" }}
                />
                {errors[key] && (
                  <Typography variant="caption" color="error">
                    {errors[key]}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Submit */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ px: 5, py: 1.2, borderRadius: 2, textTransform: "none" }}
            >
              {saving ? "Saving…" : "CREATE PRODUCT"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar open={open} autoHideDuration={3000} onClose={() => setOpen(false)}>
        <Alert severity="success" variant="filled">
          Product Created Successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddProducts;